import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { ScreenSizeDetectorService } from '@serviceplan/ui-kit';
import { AnonymousAuthService } from '@thehubz/client/anonymous-auth';
import {
  AppSettings,
  AppVersionService,
  CommonUtilityLib,
  NavigationEntry,
  NavigationItemsService,
  StorageUtilityLib,
  USER_SESSION_TOKEN,
  UserData,
  UserSession,
} from '@thehubz/client/core';
import { UserInfo } from '@thehubz/client/data-access-api';
import { ANONYMOUS_USER_EMAIL } from '@thehubz/shared/model';
import { Subscription } from 'rxjs';
import { LoginOptionPopupComponent } from '../login-option-popup/login-option-popup';

@Component({
  selector: 'lh-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  loggedInUser!: UserInfo;

  appUpdate = false;
  private appUpdateSub?: Subscription;

  lang!: string;
  isMobileNavVisible = false;

  ANONYMOUS_USER_EMAIL = ANONYMOUS_USER_EMAIL;
  dialogSelectRef!: MatDialogRef<LoginOptionPopupComponent>;

  menuItemList: NavigationEntry[] = [];

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private storageUtility: StorageUtilityLib,
    private router: Router,
    private dialog: MatDialog,
    private commonUtilityLib: CommonUtilityLib,
    private appVersionService: AppVersionService,
    private anonymousAuthService: AnonymousAuthService,
    private navigationItemService: NavigationItemsService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {
    let lang = this.storageUtility.getLocalStorageNullable<string>('lang');
    if (!lang) {
      lang = window.navigator.language.indexOf('de') !== -1 ? 'de' : 'en';
    }
    this.lang = lang;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isMobileNavVisible = false;

        // perform only for URL containing language, not e.g. localhost
        if (
          (window.location.pathname.startsWith('/en/') || window.location.pathname.startsWith('/de/')) &&
          this.lang !== window.location.pathname.substring(1, 3)
        ) {
          this.switchLanguage(this.lang);
        }
      }
    });

    this.menuItemList = this.navigationItemService.getNavigation();
  }

  ngOnInit() {
    const userSession = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken);

    const asUserInfo = (userData: UserData) => ({
      id: userData._id,
      name: userData.firstName + ' ' + userData.lastName,
      email: userData.email,
    });

    this.loggedInUser = asUserInfo(userSession.userData);
    this.appUpdateSub = this.appVersionService.versionChanged$.subscribe((value) => {
      this.appUpdate = value;
      if (value === true) {
        this.appUpdateSub?.unsubscribe();
      }
    });
  }

  logoutUser() {
    this.anonymousAuthService.logout();
  }

  navigateToProfile() {
    this.commonUtilityLib.routingObjectSetter(AppSettings.APP_CONFIG['navigation'][0].items[1]);
    this.router.navigate(['/user/profile']);
  }

  ngOnDestroy(): void {
    this.appUpdateSub?.unsubscribe();
  }

  login() {
    this.dialogSelectRef = this.dialog.open(LoginOptionPopupComponent, {
      width: '550px',
      height: '500px',
    });
    this.dialogSelectRef.disableClose = true;
    this.dialogSelectRef.afterClosed().subscribe(async (result: string) => {
      if (result && result === 'Login') {
        await this.router.navigate(['/login']);
      } else if (result && result === 'Register') {
        await this.router.navigate(['/sign-up']);
      }
    });
  }

  switchLanguage(lang: string) {
    this.storageUtility.setLocalStorage('lang', lang);
    this.lang = lang;
    if (lang === 'de') {
      window.location.href = '/de' + this.router.url;
    } else {
      window.location.href = '/en' + this.router.url;
    }
  }
}
